import React, { useState, useEffect } from 'react';
import { referenceApi } from '../../../api/reference';
import {
  facilityLookupsApi,
  FACILITY_TYPE_OPTIONS,
  FUNCTIONAL_STATUS_OPTIONS,
  OWNERSHIP_OPTIONS,
} from '../../../api/facilitiesApi';
import { useLocationOptions } from '../../Admin/shared/useLocationOptions';

const SELECT_CLS =
  'w-full bg-white border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-600 ' +
  'appearance-none cursor-pointer focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500';

const LABEL_CLS = 'block text-xs font-medium text-gray-700 mb-1';

function ChevronDown() {
  return (
    <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
}

function FilterSelect({ label: labelText, value, onChange, options, placeholder = 'All' }) {
  return (
    <div>
      <label className={LABEL_CLS}>{labelText}</label>
      <div className="relative">
        <select value={value} onChange={onChange} className={SELECT_CLS}>
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown />
      </div>
    </div>
  );
}

const EMPTY = {
  state: '', lga: '', facilityType: '', functionalStatus: '', ownership: '', validationStatus: '',
};

function toValueLabelOptions(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (typeof item === 'string') return { value: item, label: item };
      return {
        value: item.value ?? item.key ?? item.code ?? item.slug ?? item.name ?? item.label ?? '',
        label: item.label ?? item.name ?? item.display_name ?? item.value ?? item.code ?? item.slug ?? '',
      };
    })
    .filter((item) => item.value && item.label);
}

export default function FacilityCoreFilterBar({
  canCreate,
  actionLabel = 'Create New',
  onSearch,
  onFiltersChange,
  onAddRecord,
  hideFilters = false,
}) {
  const [searchInput,       setSearchInput]       = useState('');
  const [draft,             setDraft]             = useState(EMPTY);
  const [facilityTypeOpts,  setFacilityTypeOpts]  = useState(FACILITY_TYPE_OPTIONS);
  const [validationStatusOpts, setValidationStatusOpts] = useState([]);
  const { stateOptions, lgaOptions, loadingLgas } = useLocationOptions(draft.state);

  // Fetch facility type options from backend (falls back to static list if endpoint not live)
  useEffect(() => {
    facilityLookupsApi.facilityTypes().then(setFacilityTypeOpts).catch(() => {});
  }, []);

  // Load workflow status options from the shared reference endpoint.
  useEffect(() => {
    referenceApi.validationStatuses()
      .then((list) => setValidationStatusOpts(toValueLabelOptions(list)))
      .catch(() => {});
  }, []);

  const buildActiveFilters = (source) => {
    const active = Object.fromEntries(Object.entries(source).filter(([, v]) => v !== ''));
    if (searchInput.trim()) active.q = searchInput.trim();
    return active;
  };

  // Applies immediately on every change — live filtering, matching the rest
  // of the app, instead of requiring a separate "Apply Filters" click.
  const set = (key) => (e) => {
    const value = e.target.value;
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'state') next.lga = ''; // reset LGA when state changes
      onFiltersChange(buildActiveFilters(next));
      return next;
    });
  };

  const hasActiveFilters = Object.values(draft).some((v) => v !== '') || searchInput !== '';

  const handleApply = () => onFiltersChange(buildActiveFilters(draft));

  const handleReset = () => {
    setDraft(EMPTY);
    setSearchInput('');
    onSearch('');
    onFiltersChange({});
  };

  return (
    <div className="space-y-3 mb-4">

      {/* Row 1: Search + Create New */}
      <div className="flex gap-3">
        <label className="flex-1 flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2.5 bg-white cursor-text focus-within:border-green-500 focus-within:ring-1 focus-within:ring-green-500">
          <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <circle cx={11} cy={11} r={8} /><path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); onSearch(e.target.value); }}
            placeholder="Search facilities..."
            className="flex-1 text-sm text-gray-600 outline-none bg-transparent placeholder-gray-400"
          />
        </label>
        <button
          onClick={() => { onSearch(searchInput); handleApply(); }}
          className="px-5 py-2.5 bg-[#D9FBD4] border border-green-200 text-green-700 text-sm font-semibold rounded-lg hover:bg-green-100 transition-colors whitespace-nowrap"
        >
          Search
        </button>
        {canCreate && (
          <button
            onClick={onAddRecord}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-700 hover:bg-green-800 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
          >
            <span className="text-base font-bold leading-none">+</span>
            {actionLabel}
          </button>
        )}
      </div>

      {/* Row 2 & 3: Filter dropdowns + Apply/Reset — hidden for R05 */}
      {!hideFilters && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <FilterSelect label="State" value={draft.state} onChange={set('state')} options={stateOptions} />
            <FilterSelect label="LGA" value={draft.lga} onChange={set('lga')} options={lgaOptions} placeholder={loadingLgas ? 'Loading…' : 'All'} />
            <FilterSelect label="Facility Type" value={draft.facilityType} onChange={set('facilityType')} options={facilityTypeOpts} />
            <FilterSelect label="Functional Status" value={draft.functionalStatus} onChange={set('functionalStatus')} options={FUNCTIONAL_STATUS_OPTIONS} />
            <FilterSelect label="Ownership" value={draft.ownership} onChange={set('ownership')} options={OWNERSHIP_OPTIONS} />
            <FilterSelect label="Data Status" value={draft.validationStatus} onChange={set('validationStatus')} options={validationStatusOpts} />
          </div>
          <div className="flex items-center gap-3 justify-end">
            {hasActiveFilters && (
              <button onClick={handleReset} className="px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap">
                Reset
              </button>
            )}
            <button onClick={handleApply} className="px-6 py-2 bg-[#D9FBD4] border border-green-200 text-green-700 text-sm font-semibold rounded-lg hover:bg-green-100 transition-colors whitespace-nowrap">
              Apply Filters
            </button>
          </div>
        </>
      )}
    </div>
  );
}
